import type { PDFDocumentProxy } from 'pdfjs-dist/web/pdf_viewer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';

import { DealsInfo, QuotationPreview } from '../../../Types/api';
import { Deal } from '../../../Types/deal';
import { Quotation, CUSTOMER_PORTAL_STATUS } from '../../../Types/quotation';
import { getError } from '../../../Utils/errors';
import { captureException } from '../../../Utils/sentry';
import useGlobalContext from '../Context';

export interface Result {
  deal?: Deal;
  quotation?: Quotation;
  pdf?: PDFDocumentProxy;
  loading: boolean;
  error: Error | null;
}

interface CachedPdf {
  data?: PDFDocumentProxy;
  id: string;
  status: CUSTOMER_PORTAL_STATUS;
}

const useQuotation = (defaultQuotationId?: string): Result => {
  const { data, error } = useSWR<DealsInfo>('/deals.info');
  const pdfsRef = useRef<Map<string, CachedPdf>>(new Map());
  const [pdfs, setPdfs] = useState(pdfsRef.current);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const { token } = useParams<HomeRouteProps>();
  const { request } = useGlobalContext();

  const deal = data?.data;
  const quotation = useMemo(() => {
    let quotation = defaultQuotationId
      ? deal?.quotations.find((quotation) => quotation.id === defaultQuotationId)
      : deal?.quotations[0];

    if (!quotation && deal?.quotations.length === 1) {
      quotation = deal.quotations[0];
    }

    return quotation;
  }, [deal, defaultQuotationId]);

  const addPdf = (pdf: CachedPdf) => {
    pdfsRef.current.set(pdf.id, pdf);
    setPdfs(new Map([...pdfsRef.current]));
  };

  const removePdf = (id: string) => {
    pdfsRef.current.delete(id);
    setPdfs(new Map([...pdfsRef.current]));
  };

  useEffect(() => {
    (async () => {
      if (!quotation?.id) {
        return;
      }

      const cachedPdf = pdfs.get(quotation.id);
      if (
        cachedPdf &&
        (cachedPdf.status === quotation.customerPortalStatus ||
          quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.VIEWED)
      ) {
        return;
      }

      addPdf({
        data: cachedPdf?.data,
        id: quotation.id,
        status: quotation.customerPortalStatus,
      });

      let pdfjs, url;
      try {
        [pdfjs, url] = await Promise.all([
          import(/* webpackMode: "eager" */ 'pdfjs-dist/es5/build/pdf'),
          request!<QuotationPreview>('/deals.download', {
            body: JSON.stringify({
              id: quotation.id,
            }),
          }),
        ]);
      } catch (e) {
        setPdfError(getError(e));
        removePdf(quotation.id);
        captureException(e);
        return;
      }

      pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.js?v=${__PDF_WORKER_VERSION__}`;

      let pdf: PDFDocumentProxy | null = null;
      try {
        pdf = await pdfjs.getDocument({ url: url.data }).promise;
      } catch (exception) {
        try {
          // Retry fetching via the proxy
          pdf = await pdfjs.getDocument({
            url: `${__CONFIG__.API_URL}/deals.proxy?id=${quotation.id}`,
            httpHeaders: { authorization: `Bearer ${token!}` },
          }).promise;
        } catch (innerException) {
          captureException(innerException);
        }

        if (!pdf) {
          captureException(exception);

          setPdfError(getError(exception));
          removePdf(quotation.id);
          return;
        }
      }

      setPdfError(null);

      addPdf({
        data: pdf,
        id: quotation.id,
        status: quotation.customerPortalStatus,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?.id, quotation?.customerPortalStatus, request]);

  if (data && !quotation) {
    return { loading: false, error: error || pdfError, deal };
  }

  const pdf = quotation ? pdfs.get(quotation.id)?.data : undefined;
  if ((!data || !pdf) && !error && !pdfError) {
    return { loading: true, error: null, deal, quotation };
  }

  return { loading: false, error: error || pdfError, deal, quotation, pdf };
};

export default useQuotation;
